import { useState, type ReactNode } from 'react'

import { Icon } from '@/src/elements/media/Icon'
import { Modal } from '@/src/elements/modal/Modal'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useTranslate } from '@/src/lang/useTranslate'

import { usePostDeviceMutation } from '../../../services/usePostDeviceMutation'
import { subscribePush } from '../../../utils/subscribePush'
import { usePermissionTrigger } from '../../hooks/usePermissionTrigger'
import type { TOfferPushNotificationsProps } from './offerPushNotifications'
import type { TPushRequestProps as TPushRequestSharedProps } from './PushRequest.types'
import { ReactivatePush } from './ReactivatePush'

export type TPushRequestProps = TOfferPushNotificationsProps & TPushRequestSharedProps

export const PushRequest = ({ children, eventLabel, onPermissionChanged }: TPushRequestProps): ReactNode => {
  const [hasSeenPushDeniedModal, setHasSeenPushDeniedModal] = useState(false)
  const { lessThan } = useDimensions()
  const { t } = useTranslate()

  const { mutate } = usePostDeviceMutation()

  const { onTrigger, permission } = usePermissionTrigger(eventLabel, async (currentPermission, resetPermission) => {
    // Trigger push subscription only when the user granted the permission
    if (currentPermission === 'granted') {
      const pushSubscription = await subscribePush()
      if (pushSubscription) {
        mutate(pushSubscription)
      }
    }
    // Call the callback with the current permission
    onPermissionChanged?.(currentPermission)
    // Reset the permission and hasSeenPushDeniedModal state
    resetPermission()
    setHasSeenPushDeniedModal(false)
  })

  const handleClosePushDeniedModal = () => {
    // Close the modal and do not retry if the user confirmed the deny
    setHasSeenPushDeniedModal(true)
    onTrigger({ isRetry: true })
  }

  return (
    <>
      {children({ isLoading: !!permission, onTrigger })}
      {/* Do not show this modal on small screens: the native prompt is visible enough */}
      {!lessThan.md && (
        <Modal
          showModal={permission === 'default'}
          showCancelButton
          onCancel={() => onPermissionChanged(permission)}
          title={t('push.allow.message')}
        >
          <Icon name={ICON.ARROW_UP_LEFT} size="xl" className="m-auto" />
        </Modal>
      )}
      <Modal
        showModal={!hasSeenPushDeniedModal && permission === 'denied'}
        onCancel={handleClosePushDeniedModal}
        title={t('push.blocked.title')}
        description={t('push.blocked.message')}
        onConfirm={handleClosePushDeniedModal}
        showCancelButton
        confirmText={t('words.done')}
        cancelText={t('words.no-thanks')}
      >
        <ReactivatePush />
      </Modal>
    </>
  )
}
